import { capitalizeFirstLetter } from 'helpers/generalHelper';
import { Trans } from 'react-i18next';

export const filterExercisesByCategory = (data, category) => {
  return data.filter((item) => {
    return item.exercise.categories.includes(category);
  });
};

export const filterRecipesByTags = (data, tag) => {
  return data.filter((item) => {
    return item.recipe.tags.includes(tag);
  });
};

export const filterMealsByTags = (data, tag) => {
  let meals = [];
  Object.keys(data).forEach((key) => {
    data[key]['meals'].forEach((meal) => {
      if (meal['meal'] === tag && meal['recipe'] != null) {
        /*
        calories: 72055
        code: "one-pot-chicken-and-chorizo"
        image_url: "https://assets.epicurious.com/photos/585c0118427b17575654623f/6:4/w_274%2Ch_169/one-pot-chicken-and-chorizo-HC-122116.jpg"
        ingredients: []
        name: "One-Pot Chicken and Chorizo"
        nutrients: {}
        servings: 2
        tags: []
        url: "http://www.epicurious.com/recipes/food/views/one-pot-chicken-and-chorizo"
        */
        let obj = {
          calories: 0,
          code: '',
          image_url: '',
          ingredients: [],
          name: '',
          nutrients: {},
          servings: 0,
          tags: [],
          url: '',
        };
        obj['ingredients'] = meal['recipe']['ingredientLines'];
        obj['caloriesPerServing'] = meal['recipe']['caloriesPerServing'];
        obj['nutrients'] = meal['recipe']['nutrientsPerServing'];
        obj['code'] =
          meal['recipe']['source'] && meal['recipe']['source']['recipeUrl'];
        obj['image_url'] = meal['recipe']['mainImage'];
        obj['name'] = meal['recipe']['name'];
        obj['servings'] = meal['recipe']['numberOfServings'];
        obj['url'] =
          meal['recipe']['source'] && meal['recipe']['source']['recipeUrl'];
        obj['source'] =
          meal['recipe']['source'] && meal['recipe']['source']['siteUrl'];
        meals.push({
          recipe: obj,
          score: 0,
          traits: [],
        });
      }
    });
  });
  return meals;
};

const shuffleArray = (array) => {
  return array.sort(() => Math.random() - 0.5);
};

const parseTraits = (traits) => {
  return traits.map((trait) => {
    return {
      label: trait.name,
      link: trait.code,
    };
  });
};

const getCurrentDateString = () => {
  const displayLanguage = window.localStorage.getItem('lan_xh');
  const dateOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  return new Date(Date.now()).toLocaleDateString(
    displayLanguage && displayLanguage != 'undefined' ? displayLanguage : 'en',
    dateOptions
  );
};

const splitStringByCharacterIntoArrayOfStrings = (text, splitCharacter) => {
  return text.split(splitCharacter);
};

export const parseExercisesForRendering = (data) => {
  let parsedData = data.map((item) => {
    const { exercise, traits, score } = item;
    const predispositionItems = parseTraits(traits);

    const instructionsText = exercise.instructions
      .replaceAll('\n\n\n', '\n')
      .replaceAll('\n\n', '\n'); //replace unnecessary double or triple new lines characters with one

    const instructionsArray = instructionsText.includes('\n')
      ? splitStringByCharacterIntoArrayOfStrings(instructionsText, '\n')
      : [instructionsText];

    return {
      date: getCurrentDateString(),
      routinePartName: exercise.name,
      image: exercise.image_url,
      predispositionItems: predispositionItems,
      details: {
        leftGroupName: 'INSTRUCTIONS',
        leftGroupContent: { data: instructionsArray },
      },
      score: score,
    };
  });

  return shuffleArray(parsedData);
};

const parseNutrientsObjectToArray = (nutrients, calories) => {
  let highlightedNutrients = [];
  if (calories) {
    highlightedNutrients.push(
      <Trans i18nKey="dna24.traitDetails.protein" />,
      <Trans i18nKey="dna24.traitDetails.fat" />,
      <Trans i18nKey="dna24.traitDetails.carbs" />
    );
  } else {
    highlightedNutrients.push(
      <Trans i18nKey="dna24.traitDetails.protein" />,
      <Trans i18nKey="dna24.traitDetails.totalLipid" />,
      <Trans i18nKey="dna24.traitDetails.carbohydrate" />
    );
  }

  const getNutrientName = (key) => {
    let name = '';
    switch (key) {
      case 'protein':
        name = 'Protein ';
        break;
      case 'fat':
        name = 'Total lipid(fat)';
        break;
      case 'carbs':
        name = 'Carbohydrate, by difference';
        break;
      default:
        name = '';
    }
    return name;
  };

  let nutrientsArray = [];
  highlightedNutrients.forEach((key) => {
    if (calories) {
      nutrientsArray.push({
        name: getNutrientName(key),
        quantity: calories[key] ? calories[key] : '0',
        percentage: 33 + '%',
      });
    } else {
      nutrientsArray.push({
        name: key,
        quantity: nutrients[key] ? nutrients[key][0] : '0',
        percentage: nutrients[key] ? nutrients[key][1] : '0',
      });
    }
  });
  for (const key of Object.keys(nutrients)) {
    if (!highlightedNutrients.includes(key)) {
      if (calories) {
        nutrientsArray.push({
          name: capitalizeFirstLetter(key),
          quantity: nutrients[key],
          percentage: 33 + '%',
        });
      } else {
        nutrientsArray.push({
          name: key,
          quantity: nutrients[key] ? nutrients[key][0] : '0',
          percentage: nutrients[key] ? nutrients[key][1] : '0',
        });
      }
    }
  }
  return nutrientsArray;
};

export const parseRecipesForRendering = (data) => {
  let parsedData = data.map((item) => {
    const { recipe, traits, score } = item;
    const predispositionItems = parseTraits(traits);
    return {
      date: getCurrentDateString(),
      routinePartName: recipe.name,
      image: recipe.image_url,
      predispositionItems: predispositionItems,
      details: {
        leftGroupName: <Trans i18nKey="dna24.menu.recipe" />,
        leftGroupContent: {
          title: 'Ingredients',
          data: recipe.ingredients,
          source: recipe.source,
        },
        rightGroupName: <Trans i18nKey="dna24.menu.nutritionFacts" />,
        rightGroupContent: {
          calories: 300,
          stake: 2,
          carbs: 40,
          fat: 10,
          protein: 60,
          data: recipe.caloriesPerServing
            ? parseNutrientsObjectToArray(
                recipe.nutrients,
                recipe.caloriesPerServing
              )
            : parseNutrientsObjectToArray(recipe.nutrients),
        },
      },
      score: score,
      instructionsLink: recipe.code,
    };
  });

  return shuffleArray(parsedData);
};

export const filterAndParseExerciseRoutines = (
  recommenderExercises,
  category
) => {
  let exerciseRoutines = [];

  const eveningExercises = filterExercisesByCategory(
    recommenderExercises,
    category
  );
  exerciseRoutines = parseExercisesForRendering(eveningExercises);

  return exerciseRoutines;
};
